import { carrierKindTag, vesselTypeCategory } from '#details/constants';
import { isDelisted } from '#record/model';
import { RecordValidator, useGenericValidator } from '#record/validator';
import { useAppSettings } from '#settings/settings';
import { DetailContext } from '#submission/context';
import { RecordIssue } from '#submission/validator';
import { useVID } from '#vid/model';
import { unknownTenantId } from 'apprise-core/tenant';
import { useT } from 'apprise-frontend-core/intl/language';
import { useTagCache } from 'apprise-frontend-tags/cache';
import { areIntervalsOverlapping, isEqual } from 'date-fns';
import format from 'date-fns/format';
import isAfter from 'date-fns/isAfter';
import startOfDay from 'date-fns/startOfDay';
import { useContext } from 'react';
import { trxauthzType, trxAuthzTypeCategory } from './constants';
import { TrxAuthPatch } from './model';


export const useTrxAuthzValidation = (): RecordValidator<TrxAuthPatch> => {

    const validator = useTrxAuthzValidator()

    return useGenericValidator(trxauthzType, validator)
}


export const useTrxAuthzValidator = (): RecordValidator<TrxAuthPatch> => {

    const t = useT()

    const tagmap = useTagCache()
    const vid = useVID()

    const formatString = useAppSettings().resolveFormat()

    const { currentRecords, edited } = useContext(DetailContext)

    return data => data.reduce((acc, { patch }) => {

        const issues: RecordIssue[] = []

        const grantedBy = edited.tenant ?? unknownTenantId

        const { type, from, to } = patch.trxauthz.authzs[grantedBy][0]

        const fromDate = startOfDay(new Date(from))
        const toDate = startOfDay(new Date(to))

        const current = patch.uvi ? currentRecords[patch.uvi] : undefined


        if (!type)
            issues.push({ message: t('valid.missing_trx_type'), type: 'error', location: trxAuthzTypeCategory })

        if (type && !tagmap.id2tag(type).of(trxAuthzTypeCategory))
            issues.push({ message: t('valid.invalid_trx_type', { type: type }), type: 'error', location: trxAuthzTypeCategory })

        if (!from)
            issues.push({ message: t('valid.missing_from_date'), type: 'error', location: 'from' })

        if (!to)
            issues.push({ message: t('valid.missing_to_date'), type: 'error', location: 'to' })


        if (from && to && isAfter(fromDate, toDate))
            issues.push({ message: t('valid.invalid_auth_range'), type: 'error', location: 'range' })

        if (to && isAfter(Date.now(), toDate))
            issues.push({ message: t('valid.expired_auth_pastrange'), type: 'warning', location: 'range' })


        // current-based checks
        // conditional to existence, generic validator will report separately if current is missing.
        if (current) {

            const uvi = vid.valueOf(patch.uvi)

            if (current.details.vesselKind !== carrierKindTag)  // wrong type.
                issues.push({ message: t('valid.invalid_trx_kind', { uvi, type: tagmap.id2name(current.details.vesselType).of(vesselTypeCategory) }), type: 'error', location: 'uvi' })


            else if (isDelisted(current)) {  // is delisted.
                issues.push({ message: t('valid.invalid_trx_delisted', { uvi }), type: 'error', location: 'uvi' })
            }

            else {

                const now = Date.now()

                const currentFromDate = startOfDay(new Date(current.authorization.from))
                const currentToDate = startOfDay(new Date(current.authorization.to))

                if (isAfter(now, currentToDate))  // expired
                    issues.push({ message: t('valid.invalid_trx_expired', { to: format(currentToDate, formatString) }), type: 'warning', location: 'range' })

                else { // compatibility checks

                    if (to && isAfter(toDate, currentToDate))  // exceeds
                        issues.push({ message: t('valid.invalid_trx_to', { to: format(currentToDate, formatString) }), type: 'warning', location: 'to' })

                    if (from && isAfter(currentFromDate, fromDate))  // preempts
                        issues.push({ message: t('valid.invalid_trx_from', { from: format(currentFromDate, formatString) }), type: 'warning', location: 'from' })


                    const currentAuthzs = current?.trxauthz?.authzs[grantedBy]?.filter(a => a.type === type) ?? []

                    // if (revocation && !currentAuthzs.some(a => a.type === type && a.from === from && a.to === to))
                    //     issues.push({ message: t('valid.invalid_trx_revocation'), type: 'error', location: 'range' })

                    // else {

                    // const overlapping = currentAuthzs.find(current => {

                    //     const currentFrom = startOfDay(new Date(current.from))
                    //     const currentTo = startOfDay(new Date(current.to))

                    //     // return (isAfter(currentTo, fromDate) && isAfter(toDate, currentTo))    //  [...{..]...}
                    //     //     ||
                    //     //     (isAfter(currentFrom, fromDate) && isAfter(toDate, currentFrom))   // {...[...}..]

                    // })

                    const overlapping = currentAuthzs.find(current => {
                        return areIntervalsOverlapping(
                            { start: fromDate, end: toDate },
                            { start: startOfDay(new Date(current.from)), end: startOfDay(new Date(current.to)) },
                            { inclusive: true }
                        )
                    })

                    const isSameRange = currentAuthzs.find(current => {
                        const currentFrom = startOfDay(new Date(current.from))
                        const currentTo = startOfDay(new Date(current.to))
                        return isEqual(fromDate, currentFrom) && isEqual(toDate, currentTo)
                    })

                    if (isSameRange)
                        issues.push({ message: t('valid.invalid_trx_same_range'), type: 'error', location: 'range' })
                    else if (overlapping)
                        issues.push({ message: t('valid.invalid_trx_overlap', { from: format(Date.parse(overlapping.from), 'dd/MM/yyyy'), to: format(Date.parse(overlapping.to), 'dd/MM/yyyy') }), type: 'warning', location: 'range' })                     
                    

                }

            }
        }

        return { ...acc, [patch.id]: issues }

    }, {})

}


